import { useContext, useEffect, useRef, useState, type KeyboardEvent, type MutableRefObject, type ReactNode } from 'react';
import { UNSAFE_DataRouterContext, useBlocker } from 'react-router-dom';
import { Color, MODAL_FOOTER_BUTTON_SIZE, MODAL_PRIMARY_COLOR, leaveVerdict, leavesPage, t, type FormFrameHost } from '@bms2/shared';
import { Button } from '../controls';
import { AlertBanner } from '../controls/AlertBanner';
import { Modal, type ModalFooterAction } from '../modals/Modal';
import { ModalCancel, ModalShell } from '../modals/ModalShell';
import { reportCaughtInBrowser } from '../../shell/systemErrors';
import styles from './formFrame.module.css';

/**
 * The Form frame (piece 5; decision 3060). One frame draws a form section, a
 * registry form or a hand-built one, in a window body or as a section of a
 * page, and both carry the same parts:
 *
 *  - the same footer: a Primary named for the act, a Cancel, and at most one
 *    destructive-outlined secondary;
 *  - the refusal, drawn inside the frame above the fields, never behind it;
 *  - one way out for Escape, the `×`, Cancel and Back, which the leave guard
 *    holds when anything was typed (decision 3061);
 *  - focus handed back to the control that opened a window (piece 5).
 *
 * 🔨 The window shell and the page module host the frame, and neither owns it:
 * a page draws `host="section"` inside its content, and a window draws
 * `host="window"` and gets the Modal shell's title bar and pinned footer. One
 * form section is then rendered by the page and by the window alike (piece 13).
 */
export const formFrameDocumentedStates = ['window', 'section', 'refused'] as const;

/** The Primary: what the form does, in the words of the act, and the write it sends. */
export interface FormAct {
  /** 🔒 Named for the act (decision 3060): Record the Payment, Save Changes, Save and Next. */
  label: string;
  /**
   * The write. 🔒 Return its promise: the Primary is held until it settles, and
   * the leave guard stands aside while it runs, so a save that leaves the page
   * is never asked about (decision 3061).
   */
  onAct: () => unknown;
  disabled?: boolean;
  /** Why the Primary is gray, which a disabled button always says (decision 0559). */
  disabledReason?: string;
  /** Further choices behind the Primary's chevron, each its own write. */
  more?: readonly { label: string; onAct: () => unknown }[];
}

interface FormFrameBase {
  act: FormAct;
  /** The way out, after the guard: Escape, the `×`, Cancel and Back are one outcome. */
  onCancel: () => void;
  cancelLabel?: string;
  /** The one destructive-outlined secondary, where the form has one. */
  secondary?: Omit<ModalFooterAction, 'kind'>;
  /** 🔒 The refusal, drawn inside the frame above the fields (decision 3060). */
  refusal?: ReactNode;
  onDismissRefusal?: () => void;
  /** Whether anything was typed: what the leave guard reads (decision 3061). */
  typed: boolean;
  /** Which form this is, for the harness and the acceptance suites. */
  dataForm: string;
  children: ReactNode;
}

export interface FormFrameWindow extends FormFrameBase {
  host: Extract<FormFrameHost, 'window'>;
  open: boolean;
  title: string;
  subtitle?: string;
  width?: 'regular' | 'wide' | 'document';
  height?: 'content' | 'tall';
  openerId?: string | null;
}

export interface FormFrameSection extends FormFrameBase {
  host: Extract<FormFrameHost, 'section'>;
  /** The section's own heading on the page, where the page draws none above it. */
  heading?: string;
}

export type FormFrameProps = FormFrameWindow | FormFrameSection;

/** The refusal inside the frame, scrolled into sight when it lands, so a Save pressed at the foot of a long form never refuses out of view. */
function Refusal({ refusal, onDismiss }: { refusal: ReactNode; onDismiss: (() => void) | undefined }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.scrollIntoView?.({ block: 'nearest' });
  }, [refusal]);
  return (
    <div ref={ref} data-form-refusal="true">
      <AlertBanner tone="error" message={refusal} action={{ label: t('modal.dismiss'), onAction: onDismiss ?? (() => undefined) }} />
    </div>
  );
}

/**
 * An act held while its promise settles (decision 2840): the window shell does
 * this for its own footer, and a section does it here, so a double press sends
 * one write in either host.
 */
function useSettling(): { settling: boolean; settles: (act: () => unknown) => () => void } {
  const [settling, setSettling] = useState(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const settles = (act: () => unknown) => () => {
    const outcome = act();
    if (outcome && typeof (outcome as PromiseLike<unknown>).then === 'function') {
      setSettling(true);
      const done = () => {
        if (mounted.current) setSettling(false);
      };
      (outcome as PromiseLike<unknown>).then(done, (error: unknown) => {
        done();
        reportCaughtInBrowser('FormFrame.act', error);
      });
    }
  };
  return { settling, settles };
}

/**
 * 🔒 The leave question (decision 3061): Leave Without Saving?, with Keep
 * Editing and Leave. The title is the one the Settings pages already ask.
 * 🔨 Drawn with the Modal shell, never `confirm()`, so it stacks over the window
 * it asks about and hands focus back to it.
 */
function LeaveQuestion({ onLeave, onKeep }: { onLeave: () => void; onKeep: () => void }) {
  return (
    <ModalShell
      open
      title={t('settings.leave.title')}
      onCancel={onKeep}
      dataModal="form-leave"
      footer={(
        <>
          <ModalCancel label={t('form.leave.keep')} onCancel={onKeep} />
          <Button size={MODAL_FOOTER_BUTTON_SIZE} color={MODAL_PRIMARY_COLOR} text={[t('form.leave.leave')]} actions={[onLeave]} />
        </>
      )}
    >
      <p data-form-leave="true">{t('form.leave.body')}</p>
    </ModalShell>
  );
}

/**
 * 🔒 Back, a link and the side menu are held while a typed form stands on the
 * page (decision 3061), and the same question asks first. The browser's own
 * close and reload get the browser's own prompt, because a page cannot draw
 * over its own unloading. 🔨 A move inside the page is never held, and nor is
 * a navigation the form's own act makes while its write is in flight.
 */
function NavigationHold({ acting }: { acting: MutableRefObject<boolean> }) {
  const blocker = useBlocker(({ currentLocation, nextLocation }) => !acting.current && leavesPage(currentLocation.pathname, nextLocation.pathname));
  useEffect(() => {
    const hold = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // The older browsers ask only when the event carries a return value.
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', hold);
    return () => window.removeEventListener('beforeunload', hold);
  }, []);
  if (blocker.state !== 'blocked') return null;
  return <LeaveQuestion onLeave={() => blocker.proceed?.()} onKeep={() => blocker.reset?.()} />;
}

/**
 * The frame's own field layout on its own (T3363): one column, the one fixed gap between a field and the next label.
 * A field group that sits in a window or a page section the frame does not host, such as a standard form's date and name
 * above an upload's drop zone, draws its fields through this and not a grid of its own.
 */
export function FormFields({ children, dataFields }: { children: ReactNode; dataFields?: string }) {
  return <div className={styles.form} data-form-fields={dataFields ?? 'true'}>{children}</div>;
}

export function FormFrame(props: FormFrameProps) {
  const { act, onCancel, cancelLabel = t('modal.cancel'), secondary, refusal = null, onDismissRefusal, typed, dataForm, children } = props;
  // 🔒 A closed window holds nothing to lose, whatever its caller still keeps (decision 3061).
  const live = props.host === 'window' ? props.open && typed : typed;
  const [asking, setAsking] = useState(false);
  /** Whether the form's own act is in flight: a navigation it makes, such as opening the record it just created, is never held. */
  const acting = useRef(false);
  /** Runs the form's own act with the hold standing aside until its promise settles; called on a press, never while drawing. */
  const acted = (run: () => unknown): unknown => {
    acting.current = true;
    const outcome = run();
    if (outcome && typeof (outcome as PromiseLike<unknown>).then === 'function') {
      const done = () => {
        acting.current = false;
      };
      (outcome as PromiseLike<unknown>).then(done, /* silent: the form's act returns this promise to its caller, who answers a rejection. */ done);
    } else acting.current = false;
    return outcome;
  };
  const guardedAct: FormAct = { ...act, onAct: () => acted(act.onAct), ...(act.more ? { more: act.more.map((choice) => ({ ...choice, onAct: () => acted(choice.onAct) })) } : {}) };
  /**
   * 🔨 Every way out passes through here (decision 3061): Escape, the `×` and
   * Cancel close an untouched form at once and ask of a typed one. A press on
   * the backdrop still does nothing in a window with a Cancel (piece 5), so it
   * never needs to ask.
   */
  const leave = () => {
    if (leaveVerdict(live) === 'ask') setAsking(true);
    else onCancel();
  };
  const question = asking ? <LeaveQuestion onLeave={() => { setAsking(false); onCancel(); }} onKeep={() => setAsking(false)} /> : null;
  // A navigation is held only under the application's own router; a form drawn alone, as a test draws it, has none to hold.
  const dataRouter = useContext(UNSAFE_DataRouterContext);
  const hold = live && dataRouter ? <NavigationHold acting={acting} /> : null;
  const shownRefusal = refusal !== null && refusal !== undefined && refusal !== '' ? <Refusal refusal={refusal} onDismiss={onDismissRefusal} /> : null;

  if (props.host === 'window') {
    const { open, title, subtitle, width = 'regular', height = 'content', openerId = null } = props;
    const actions: ModalFooterAction[] = [
      { kind: 'primary', ...guardedAct },
      { kind: 'cancel', label: cancelLabel, onAct: leave },
      ...(secondary ? [{ kind: 'destructiveSecondary' as const, ...secondary, onAct: () => acted(secondary.onAct) }] : []),
    ];
    return (
      <Modal open={open} title={title} {...(subtitle === undefined ? {} : { subtitle })} onCancel={leave} actions={actions} width={width} height={height} openerId={openerId} dataModal={dataForm}>
        <div className={styles.form} data-form-frame="window" data-form={dataForm} data-form-typed={live ? 'true' : 'false'}>
          {open ? shownRefusal : null}
          {children}
        </div>
        {/* The questions stack over the window they ask about, inside it, so the stacking rule holds the window inert beneath. */}
        {open ? question : null}
        {hold}
      </Modal>
    );
  }
  return (
    <>
      <FrameSection {...props} act={guardedAct} {...(secondary ? { secondary: { ...secondary, onAct: () => acted(secondary.onAct) } } : {})} leave={leave} cancelLabel={cancelLabel} shownRefusal={shownRefusal} typed={live} />
      {question}
      {hold}
    </>
  );
}

/** The frame as a section of a page: the same fields, the same footer at its foot, and Escape as its way out. */
function FrameSection({ heading, act, secondary, dataForm, children, leave, cancelLabel, shownRefusal, typed }: FormFrameSection & { leave: () => void; cancelLabel: string; shownRefusal: ReactNode }) {
  const { settling, settles } = useSettling();
  /**
   * 🔨 Escape inside the section is its way out, as in a window, except where
   * a list a control opened takes it first: a dropdown's Escape closes the list
   * and leaves the form open.
   */
  const escape = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Escape' || event.defaultPrevented || settling) return;
    if (event.target instanceof Element && event.target.closest('[aria-expanded="true"]')) return;
    event.preventDefault();
    leave();
  };
  return (
    <section className={styles.section} data-form-frame="section" data-form={dataForm} data-form-typed={typed ? 'true' : 'false'} onKeyDown={escape}>
      {heading ? <h2 className={styles.heading}>{heading}</h2> : null}
      <div className={styles.form}>
        {shownRefusal}
        {children}
      </div>
      <div className={styles.footer} data-form-footer="true">
        {secondary ? (
          <span className={styles.secondary}>
            <Button
              size={MODAL_FOOTER_BUTTON_SIZE}
              color={Color.Destructive}
              text={[secondary.label]}
              actions={[secondary.onAct]}
              disabled={settling || (secondary.disabled ?? false)}
              {...(secondary.confirm ? { confirm: secondary.confirm } : { destructive: secondary.destructive ?? { subject: secondary.label, phrase: t('modal.deletePhrase') } })}
              {...(secondary.disabledReason === undefined ? {} : { disabledReason: secondary.disabledReason })}
            />
          </span>
        ) : null}
        <ModalCancel label={cancelLabel} onCancel={leave} disabled={settling} />
        <Button
          size={MODAL_FOOTER_BUTTON_SIZE}
          color={MODAL_PRIMARY_COLOR}
          text={[act.label, ...(act.more ?? []).map((choice) => choice.label)]}
          actions={[settles(act.onAct), ...(act.more ?? []).map((choice) => settles(choice.onAct))]}
          disabled={settling || (act.disabled ?? false)}
          {...(act.disabledReason === undefined ? {} : { disabledReason: act.disabledReason })}
        />
      </div>
    </section>
  );
}
